import { ReactElement, useContext, useEffect, useState } from 'react';
import React from 'react';
import {
  getAuth,
  sendPasswordResetEmail,
  onAuthStateChanged,
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useRouter } from './RouterContext';
import { get, getDatabase, ref, update } from 'firebase/database';
import { firebaseApp } from '../index';
import { getSrikandiChildren } from '../API';

const AuthContext = React.createContext<IAuth>({});

interface DBUser {
  id: string;
  email: string;
  name: string;
  parent: string;
  shopify_user_id: string;
  role: 'user' | 'srikandi' | 'srikandi_emas' | 'admin';
  profile: any;
  agreement: any;
  childrenIds: string[];
}

export interface IAuth {
  loading?: boolean;
  user?: User | null;
  dbUser?: DBUser | null;
  onLogin?: (email: string, password: string) => Promise<void>;
  onRegister?: (email: string, password: string) => Promise<void>;
  onLogout?: () => Promise<void>;
  onForgotPassword?: (email: string) => Promise<void>;
}

export const AuthProvider = ({ children }: { children: ReactElement<any, any> }) => {
  const auth = getAuth();
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [dbUser, setDBUser] = useState<DBUser | null | undefined>(undefined);
  const { route } = useRouter();
  const navigate = useNavigate();

  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        setUser(u);
      }),
    [],
  );

  useEffect(() => {
    if (user) {
      const database = getDatabase(firebaseApp);
      const userRef = ref(database, `users/${user.uid}`);
      get(userRef).then(async (snapshot) => {
        setDBUser(
          snapshot.exists()
            ? {
                id: snapshot.key,
                childrenIds:
                  (await getSrikandiChildren(snapshot.key || '').catch(() => [])).map(
                    (e) => e.id,
                  ) || [],
                ...snapshot.val(),
              }
            : null,
        );

        // set display name to database name
        if (!snapshot.val()?.name && user.displayName) {
          await update(userRef, { name: user.displayName });
        }
      });
    } else if (user === null) {
      setDBUser(null);
    }
  }, [user]);

  const handleLogin = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    navigate(route.from || '/');
  };

  const handleRegister = async (email: string, password: string) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(user);
    alert('Check your email for verification before you can use the app');
    navigate(route.from || '/');
  };

  const handleForgotPassword = async (email: string) => {
    if (!email) {
      alert('Please enter a valid email');
    } else {
      await sendPasswordResetEmail(auth, email).catch((err) => {
        alert((err as any).toString());
      });
      alert(
        `Password reset link has been sent to ${email}! Please check your spam if it's not found in inbox`,
      );
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
  };

  const value = {
    user,
    dbUser,
    loading: user === undefined || dbUser === undefined,
    onLogin: handleLogin,
    onLogout: handleLogout,
    onRegister: handleRegister,
    onForgotPassword: handleForgotPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
